import { createBrowserClient } from '@supabase/ssr';
import { SupabaseClient } from '@supabase/supabase-js';
import { getEnv } from '@/lib/utils/env';

const supabaseUrl = getEnv('VITE_SUPABASE_URL', false);
const supabaseKey = getEnv('VITE_SUPABASE_PUBLISHABLE_DEFAULT_KEY', false);

if (!supabaseUrl || !supabaseKey) {
  console.warn('Supabase credentials not configured. Some features may not work.');
}

/**
 * Cookie domain for cross-subdomain SSO across *.holito.app.
 * Using a leading dot allows the cookie to be shared with all subdomains.
 */
const COOKIE_DOMAIN = '.holito.app';

/**
 * Determines if we're in local development (localhost, 127.0.0.1, etc.).
 * Local dev doesn't use cross-domain cookies or Secure flag (HTTP).
 */
function isLocalDevelopment(): boolean {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.endsWith('.localhost') ||
    hostname.startsWith('192.168.')
  );
}

// Singleton client instance
let supabaseClient: SupabaseClient | null = null;

/**
 * Returns the singleton Supabase client configured with cookie-based auth storage.
 * Uses @supabase/ssr createBrowserClient which properly handles:
 * - Chunking session data across multiple cookies (handles >4KB sessions)
 * - Cross-subdomain SSO via configurable cookie domain
 * - Proper Base64-URL encoding for cookie compliance
 *
 * @throws Error if Supabase credentials are not configured
 */
export function getSupabase(): SupabaseClient {
  if (!supabaseClient) {
    if (!supabaseUrl || !supabaseKey) {
      throw new Error(
        'Supabase credentials not configured. Please set VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_DEFAULT_KEY in your .env file.'
      );
    }

    const isLocal = isLocalDevelopment();

    supabaseClient = createBrowserClient(supabaseUrl, supabaseKey, {
      cookieOptions: {
        domain: isLocal ? undefined : COOKIE_DOMAIN,
        path: '/',
        sameSite: 'lax',
        secure: !isLocal,
      },
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
      },
    });
  }
  return supabaseClient;
}
